import type { ContactFieldDefinition } from "./audience_helper";

/** Operadores expostos na UI — espelham ContactFieldValueService (LAC-103). */
export type FieldFilterOp =
  | "eq"
  | "contains"
  | "gte"
  | "lte"
  | "between"
  | "in";

export type FieldFilterEntry = {
  op: FieldFilterOp;
  value: string;
  /** Intervalo numérico/data quando op=between. */
  valueTo?: string;
};

/** Payload GET ?fields[slug]= ou ?fields[slug][op]=… */
export type ContactFieldsQueryValue =
  | string
  | {
      op: FieldFilterOp;
      value: string | string[];
      value_to?: string;
    };

export function operatorsForFieldType(
  type: ContactFieldDefinition["type"]
): FieldFilterOp[] {
  switch (type) {
    case "text":
    case "textarea":
      return ["eq", "contains"];
    case "number":
    case "date":
      return ["eq", "gte", "lte", "between"];
    case "select":
    case "multiselect":
      return ["eq", "in"];
    case "boolean":
      return ["eq"];
    default:
      return ["eq"];
  }
}

export function defaultOpForFieldType(
  type: ContactFieldDefinition["type"]
): FieldFilterOp {
  const ops = operatorsForFieldType(type);
  if (type === "text" || type === "textarea") {
    return "contains";
  }
  if (type === "multiselect") {
    return "in";
  }
  return ops[0] ?? "eq";
}

export function isFieldFilterActive(entry?: FieldFilterEntry): boolean {
  if (!entry) {
    return false;
  }
  if (entry.op === "between") {
    return (
      entry.value.trim() !== "" && (entry.valueTo?.trim() ?? "") !== ""
    );
  }
  if (entry.op === "in") {
    return parseInValues(entry.value).length > 0;
  }
  return entry.value.trim() !== "";
}

export function parseInValues(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function buildFieldsQueryParam(
  filters: Record<string, FieldFilterEntry>
): Record<string, ContactFieldsQueryValue> | undefined {
  const out: Record<string, ContactFieldsQueryValue> = {};

  for (const [slug, entry] of Object.entries(filters)) {
    if (!isFieldFilterActive(entry)) {
      continue;
    }

    if (entry.op === "eq") {
      out[slug] = entry.value.trim();
      continue;
    }

    if (entry.op === "between") {
      out[slug] = {
        op: "between",
        value: entry.value.trim(),
        value_to: (entry.valueTo ?? "").trim(),
      };
      continue;
    }

    if (entry.op === "in") {
      out[slug] = {
        op: "in",
        value: parseInValues(entry.value),
      };
      continue;
    }

    out[slug] = {
      op: entry.op,
      value: entry.value.trim(),
    };
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export function operatorLabel(
  op: FieldFilterOp,
  t: Translate
): string {
  return t(`contacts.filter.operator.${op}`);
}

export function formatFieldFilterChip(
  def: ContactFieldDefinition,
  entry: FieldFilterEntry,
  t: Translate
): string {
  let display = entry.value;
  if (def.type === "boolean") {
    display =
      entry.value === "true"
        ? t("contacts.fields.booleanTrue")
        : t("contacts.fields.booleanFalse");
  } else if (entry.op === "in") {
    display = parseInValues(entry.value).join(", ");
  } else if (entry.op === "between") {
    display = `${entry.value} – ${entry.valueTo ?? ""}`;
  }

  if (entry.op === "eq" && def.type !== "boolean") {
    return `${def.label}: ${display}`;
  }

  return `${def.label} (${operatorLabel(entry.op, t)}): ${display}`;
}
